import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, throttle } from 'underscore';
import globalWindowSize from './global-window-size';

type TTrackScrollPosition = {
  containerRef?: RefObject<HTMLElement | null> | null;
  trackWindow?: boolean;
  debounceTime?: number;
  throttleTime?: number;
  trackType?: 'debounced' | 'throttled' | 'fast' | 'debounced_and_throttled';
  dimensionType?: 'client' | 'offset';
  rerunCounter?: number;
  threshold?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  pause?: boolean;
};

const useTrackScrollPosition = ({
  containerRef,
  debounceTime = 50,
  throttleTime = 250,
  rerunCounter = 0,
  trackWindow,
  threshold,
  trackType = 'fast',
  dimensionType = 'offset',
  pause,
}: TTrackScrollPosition) => {
  const thresholdTop = useMemo(() => threshold?.top || 0, [threshold?.top]);
  const thresholdBottom = useMemo(() => threshold?.bottom || 0, [threshold?.bottom]);
  const thresholdLeft = useMemo(() => threshold?.left || 0, [threshold?.left]);
  const thresholdRight = useMemo(() => threshold?.right || 0, [threshold?.right]);

  const calculatePositions = useCallback(() => {
    if (trackWindow) {
      const isTop = window.scrollY <= thresholdTop;
      const isBottom =
        window.scrollY + globalWindowSize.innerHeight >= document.documentElement.scrollHeight - thresholdBottom;
      const isLeft = window.scrollX <= thresholdLeft;
      const isRight =
        window.scrollX + globalWindowSize.innerWidth >= document.documentElement.scrollWidth - thresholdRight;

      return {
        isTop,
        isBottom,
        isLeft,
        isRight,
      };
    }

    const el = containerRef?.current;

    // if container doesn't exist, assume we are at edge of each direction
    if (!el) {
      return {
        isTop: true,
        isBottom: true,
        isLeft: true,
        isRight: true,
      };
    }

    const shouldUseOffset = dimensionType === 'offset';

    const scrollTop = el.scrollTop;
    const scrollLeft = el.scrollLeft;
    const offsetHeight = shouldUseOffset ? el.offsetHeight : el.clientHeight;
    const scrollHeight = el.scrollHeight - thresholdBottom;
    const offsetWidth = shouldUseOffset ? el.offsetWidth : el.clientWidth;
    const scrollWidth = el.scrollWidth - thresholdRight;

    const isTop = scrollTop <= thresholdTop;
    const isBottom = Math.ceil(offsetHeight + scrollTop) >= scrollHeight;
    const isLeft = scrollLeft <= thresholdLeft;
    const isRight = Math.ceil(offsetWidth + scrollLeft) >= scrollWidth;

    return {
      isTop,
      isBottom,
      isLeft,
      isRight,
    };
  }, [containerRef, dimensionType, thresholdBottom, thresholdLeft, thresholdRight, thresholdTop, trackWindow]);

  const initStates = useMemo(() => calculatePositions(), [calculatePositions]);

  const frameRef = useRef(0);
  const [isTop, setIsTop] = useState(initStates.isTop);
  const [isBottom, setIsBottom] = useState(initStates.isBottom);
  const [isLeft, setIsLeft] = useState(initStates.isLeft);
  const [isRight, setIsRight] = useState(initStates.isRight);

  useEffect(() => {
    if (pause) return;

    const el = containerRef?.current;

    if (!el && !trackWindow) return;

    const fastThrottled = () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = requestAnimationFrame(() => {
        const results = calculatePositions();
        setIsTop(results.isTop);
        setIsBottom(results.isBottom);
        setIsLeft(results.isLeft);
        setIsRight(results.isRight);
      });
    };

    fastThrottled();

    const debounced = debounce(fastThrottled, debounceTime);
    const throttled = throttle(fastThrottled, throttleTime);

    const isDebounced = trackType === 'debounced' || trackType === 'debounced_and_throttled';
    const isThrottled = trackType === 'throttled' || trackType === 'debounced_and_throttled';
    const isFast = trackType === 'fast';

    if (el) {
      if (isDebounced) {
        el.addEventListener('scroll', debounced, { passive: true });
      }
      if (isThrottled) {
        el.addEventListener('scroll', throttled, { passive: true });
      }
      if (isFast) {
        el.addEventListener('scroll', fastThrottled, { passive: true });
      }
    } else {
      if (isDebounced) {
        window.addEventListener('scroll', debounced, { passive: true });
      }
      if (isThrottled) {
        window.addEventListener('scroll', throttled, { passive: true });
      }
      if (isFast) {
        window.addEventListener('scroll', fastThrottled, { passive: true });
      }
    }

    return () => {
      if (el) {
        el.removeEventListener('scroll', debounced);
        el.removeEventListener('scroll', throttled);
        el.removeEventListener('scroll', fastThrottled);
      }
      window.removeEventListener('scroll', debounced);
      window.removeEventListener('scroll', throttled);
      window.removeEventListener('scroll', fastThrottled);
    };
  }, [rerunCounter, containerRef, debounceTime, throttleTime, trackType, trackWindow, calculatePositions, pause]);

  return useMemo(
    () => ({
      isTop,
      isBottom,
      isLeft,
      isRight,
    }),
    [isBottom, isLeft, isRight, isTop]
  );
};

export default useTrackScrollPosition;
